import { useNavigate } from 'react-router-dom';

const AIStressTestingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0D0D0D]">
      {/* Back Button */}
      <button
        onClick={() => { navigate('/'); window.scrollTo(0, 0); }}
        data-testid="back-to-home"
        className="fixed top-6 left-6 z-50 flex items-center space-x-2 text-white hover:text-[#00E600] transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        <span className="font-semibold">Back to Home</span>
      </button>

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center px-6 pt-24 pb-16">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1697577418970-95d99b5a55cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlfGVufDB8fHx8MTc2MTA0Njc0OHww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}></div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 uppercase tracking-wide">
            AI <span className="text-[#00E600]">Stress Testing</span>
          </h1>
          <p className="text-xl text-[#B3B3B3] leading-relaxed">
            When failure isn't an option, we put your AI systems through hell—so reality doesn't have to.
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-8 uppercase tracking-wide">Overview</h2>
          <div className="space-y-6 text-lg text-[#B3B3B3] leading-relaxed">
            <p className="text-xl text-white font-semibold">
              Your AI system works perfectly in testing. But what happens when millions of users hit it simultaneously? 
              When data patterns shift overnight? When edge cases emerge that your training never predicted?
            </p>
            <p>
              NYAXO's AI Stress Testing doesn't just validate performance—it hunts for weaknesses. We subject your 
              artificial intelligence systems to extreme conditions, adversarial attacks, and real-world chaos that 
              would cripple unprepared systems.
            </p>
            <p>
              Our testing frameworks have validated billion-dollar AI deployments, uncovering critical vulnerabilities 
              before they cost enterprises their reputation—or worse, their business.
            </p>
          </div>
        </div>
      </section>

      {/* What Our Platform Does */}
      <section className="py-16 px-6 bg-[#0D0D0D]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 uppercase tracking-wide">
              Enterprise AI <span className="text-[#00E600]">Testing Platform</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg max-w-3xl mx-auto">
              Comprehensive validation ensuring your AI systems perform flawlessly under real-world conditions
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Model Performance Testing</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• Cross-model comparison (GPT, Claude, Gemini)</li>
                <li>• 100+ automated test scenarios</li>
                <li>• Response time & accuracy metrics</li>
                <li>• Hallucination detection</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 7H7v6h6V7z" />
                  <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Load & Stress Testing</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• High-volume concurrent requests</li>
                <li>• Degraded condition analysis</li>
                <li>• Breaking point identification</li>
                <li>• Reliability scoring (0-100)</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Security & Compliance</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• Prompt injection testing</li>
                <li>• PII/GDPR/HIPAA validation</li>
                <li>• Bias & toxicity detection</li>
                <li>• Automated audit trails</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Adversarial Testing</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• Edge case discovery</li>
                <li>• Jailbreak attempts</li>
                <li>• Vulnerability scanning</li>
                <li>• Red team simulation</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Cost Optimization</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• Token usage analysis</li>
                <li>• Cost-per-query tracking</li>
                <li>• ROI calculations</li>
                <li>• Model efficiency recommendations</li>
              </ul>
            </div>

            <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center mb-4 group-hover:shadow-[0_0_20px_rgba(0,230,0,0.5)] transition-all duration-300">
                <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Real-Time Monitoring</h3>
              <ul className="space-y-2 text-sm text-[#B3B3B3]">
                <li>• Production performance tracking</li>
                <li>• Instant anomaly alerts</li>
                <li>• Continuous validation</li>
                <li>• SCADA integration</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 uppercase tracking-wide">
              Industries We <span className="text-[#00E600]">Empower</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'Financial Services', challenge: 'Regulatory compliance', benefit: 'Pass audits, prevent fines' },
              { name: 'Healthcare', challenge: 'Patient data protection', benefit: 'Zero breaches, HIPAA compliant' },
              { name: 'E-Commerce', challenge: 'Revenue impact', benefit: '40% conversion improvement' },
              { name: 'SaaS & Technology', challenge: 'Fast releases', benefit: '3x faster deployment' },
              { name: 'Customer Service', challenge: 'Inconsistent responses', benefit: '60% fewer escalations' },
              { name: 'Legal & Compliance', challenge: 'High accuracy needs', benefit: '99.9% precision' }
            ].map((industry, idx) => (
              <div key={idx} className="bg-[#0D0D0D] border border-[#2F2F2F] rounded-lg p-6 hover:border-[#00E600] transition-all duration-300 group">
                <div className="w-10 h-10 bg-gradient-to-br from-[#00E600]/20 to-[#36E0A8]/20 rounded-lg flex items-center justify-center mb-4 border border-[#00E600]/30 group-hover:border-[#00E600] transition-all duration-300">
                  <div className="w-2 h-2 bg-[#00E600] rounded-full"></div>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{industry.name}</h3>
                <p className="text-sm text-[#B3B3B3] mb-2"><span className="text-white font-semibold">Challenge:</span> {industry.challenge}</p>
                <p className="text-sm text-[#00E600] font-semibold">→ {industry.benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why NYAXO */}
      <section className="py-16 px-6 bg-[#0D0D0D]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4 uppercase tracking-wide">
              Why <span className="text-[#00E600]">NYAXO</span> Leads
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#1A1A1A] border-l-4 border-[#00E600] rounded-lg p-8">
              <h3 className="text-2xl font-bold text-white mb-6">What Sets Us Apart</h3>
              <div className="space-y-4 text-[#B3B3B3]">
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">Enterprise-First:</span> Built for executives, not just developers</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">Vendor-Agnostic:</span> Test all models in one platform</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">Compliance-Driven:</span> GDPR, HIPAA, SOC 2 built-in</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">20+ Years Heritage:</span> SimNet™ infrastructure testing expertise</p>
                <p><span className="text-[#00E600]">→</span> <span className="text-white font-semibold">White-Glove Service:</span> Expert consulting, not just software</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6">
                <h4 className="text-xl font-bold text-white mb-2">Cost Savings</h4>
                <div className="text-4xl font-bold text-[#00E600] mb-2">25-40%</div>
                <p className="text-[#B3B3B3]">Average reduction in AI operational costs</p>
              </div>
              <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6">
                <h4 className="text-xl font-bold text-white mb-2">Failure Prevention</h4>
                <div className="text-4xl font-bold text-[#00E600] mb-2">90%</div>
                <p className="text-[#B3B3B3]">Production incidents prevented before deployment</p>
              </div>
              <div className="bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg p-6">
                <h4 className="text-xl font-bold text-white mb-2">ROI</h4>
                <div className="text-4xl font-bold text-[#00E600] mb-2">800%+</div>
                <p className="text-[#B3B3B3]">Typical return on investment for enterprises</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#1A1A1A]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Stress Test Your AI?</h2>
          <p className="text-lg text-[#B3B3B3] mb-8">
            Don't wait for reality to expose your AI's weaknesses. Let NYAXO find them first.
          </p>
          <button
            onClick={() => { navigate('/#contact'); window.scrollTo(0, 0); }}
            className="px-8 py-4 bg-gradient-to-r from-[#00E600] to-[#36E0A8] text-[#0D0D0D] font-bold rounded-md hover:shadow-[0_0_30px_rgba(0,230,0,0.6)] transition-all duration-300 uppercase tracking-wide"
          >
            Schedule a Consultation
          </button>
        </div>
      </section>
    </div>
  );
};

export default AIStressTestingPage;
